import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock3, MapPin, Package, Printer, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { orderApi } from "@/features/commerce/api";
import { ROUTES } from "@/constants/routes";

function fINR(n: number) {
    return `₹${n.toLocaleString("en-IN")}`;
}

export default function OrderSuccess() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: order, isLoading } = useQuery({
        queryKey: ["order", id],
        queryFn: () => orderApi.get(id),
    });

    if (isLoading || !order) {
        return (
            <div className="tsgo-container py-16 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                <p className="mt-3 text-sm text-muted-foreground">Confirming your order…</p>
            </div>
        );
    }

    const eta = new Date(order.estimated_delivery_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    return (
        <div data-testid="order-success-page" className="tsgo-page">
            <section className="tsgo-container pt-8 sm:pt-12">
                <div className="mx-auto max-w-2xl overflow-hidden rounded-3xl border border-border/70 bg-card p-6 shadow-soft sm:p-10">
                    <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand text-white shadow-brand">
                        <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <h1 className="mt-5 text-center text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="order-success-title">
                        Order placed!
                    </h1>
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                        Order <b data-testid="order-number">{order.number}</b> — {fINR(order.breakdown.total)}
                    </p>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        <InfoRow icon={<Clock3 className="h-4 w-4" />} label="Estimated delivery" value={`by ${eta}`} />
                        <InfoRow icon={<MapPin className="h-4 w-4" />} label="Delivery to" value={`${order.address.line1}, ${order.address.city}`} />
                        <InfoRow icon={<Package className="h-4 w-4" />} label="Items" value={`${order.items.length}`} />
                        <InfoRow icon={<ShoppingBag className="h-4 w-4" />} label="Payment" value={`${(order.payment.method || "").toUpperCase()} · ${order.payment.status}`} />
                    </div>

                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            onClick={() => navigate(ROUTES.orderDetailBuild(order.id))}
                            className="bg-brand text-white hover:bg-brand-600"
                            data-testid="order-success-track"
                        >
                            Track order
                        </Button>
                        <Button variant="outline" onClick={() => window.print()} data-testid="order-success-print">
                            <Printer className="mr-1 h-4 w-4" /> Print invoice
                        </Button>
                        <Link to={ROUTES.home} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-brand/40 hover:text-brand" data-testid="order-success-home">
                            Continue shopping
                        </Link>
                    </div>
                </div>

                {/* Simple invoice layout for print */}
                <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-border/70 bg-card p-6 print:border-0">
                    <h3 className="tsgo-h3">Invoice</h3>
                    <p className="text-xs text-muted-foreground">Order #{order.number}</p>
                    <ul className="mt-3 divide-y divide-border/70">
                        {order.items.map((it) => (
                            <li key={it.product_id} className="flex justify-between py-2 text-sm">
                                <span>{it.name} × {it.quantity}</span>
                                <span className="font-semibold">{fINR(it.line_total)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 space-y-1 text-sm">
                        <RowLine label="Subtotal" value={fINR(order.breakdown.subtotal)} />
                        <RowLine label="Delivery" value={order.breakdown.delivery_charge ? fINR(order.breakdown.delivery_charge) : "FREE"} />
                        <RowLine label="Platform fee" value={fINR(order.breakdown.platform_fee)} />
                        <RowLine label="Handling fee" value={fINR(order.breakdown.handling_fee)} />
                        <RowLine label="GST" value={fINR(order.breakdown.gst)} />
                        {order.breakdown.coupon_discount > 0 ? (
                            <RowLine label={`Coupon ${order.breakdown.coupon_code}`} value={`- ${fINR(order.breakdown.coupon_discount)}`} />
                        ) : null}
                        <div className="mt-1 flex justify-between border-t border-border/70 pt-2 text-base font-extrabold">
                            <span>Total</span>
                            <span>{fINR(order.breakdown.total)}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/30 p-3">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand/10 text-brand">{icon}</span>
            <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="truncate text-sm font-semibold">{value}</p>
            </div>
        </div>
    );
}

function RowLine({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold">{value}</span>
        </div>
    );
}
